import Head from 'next/head';
import { FC, useMemo, useEffect, useState } from 'react';
import { useAction } from '@reatom/npm-react';
import { pushNotification } from '@whitesharx/starfall-frontend-ui-kit';
import {
  useServices,
  t,
  WeeklyRewards,
  useSettingsQuery,
  queryClient,
  ShopCoin,
  usePlayerCoins,
  BUY_COINS_BUTTON_ID,
  Player,
  VIEW_EVENTS,
  settingsRequest,
  usePlayerQuery,
  hasVIP,
  convertToPlainNumbers,
} from '@whitesharx/starfall-frontend-core';
import { Shop, BuyCoinsModal } from '@whitesharx/starfall-frontend-core/shop_screen';
import '@whitesharx/starfall-frontend-core/shop_screen.css';

import UserInfo from '~/src/components/UserInfo';
import PageLayoutHeader from '~/src/components/PageLayoutHeader';
import ChessRoyaleBanner from '~/src/components/ChessRoyaleBanner';
import InfoHeader from '~/src/components/InfoHeader';

import { AppComponentType, AppTypes } from './_app';
import { formatCoins } from '~/src/utils/products';
import { bannerContainerStyles } from '~/styles/pages/common.css';
import CommonCustomApp from '~/src/components/CustomApp/CommonCustomApp';
import ShopSkeletonPage, { ShopSkeletonPageProps } from '~/src/skeletons/Shop';
import { VIPBanner } from '~/src/screens/Shop/components/VIPBanner';
import { updateRightContainerAction } from '~/src/models/layout';

type ShopPageProps = { shop: ShopSkeletonPageProps };

const ShopRealPage: FC<ShopPageProps> = ({ shop }) => {
  const { useResponsive, Adsense, Analytics, usePayments } = useServices();
  const { isDesktop } = useResponsive();
  const { buyCoins, loading } = usePayments();
  const updateRightContainer = useAction(updateRightContainerAction);

  const [coinsPack, setCoinsPack] = useState<ShopCoin | null>(null);
  const updatePlayer = (player: Player) => queryClient.setQueryData('player', { player }, { updatedAt: Date.now() });

  const settingsQuery = useSettingsQuery();
  const { data: playerData } = usePlayerQuery();

  const coinsData = settingsQuery.data?.shop.purchaseCoins;
  const coins = useMemo(() => formatCoins(coinsData), [coinsData]);

  const { animateCoins } = usePlayerCoins();

  const rightContainer = useMemo(() => {
    if (isDesktop) {
      return (
        <>
          <UserInfo />
          {Adsense.renderBanner({
            placement: 'shop_screen_banner_right',
            slot: 'bannerRight',
            withSafeMargins: true,
          })}
          <div className={bannerContainerStyles}>
            <ChessRoyaleBanner />
          </div>
        </>
      );
    }
  }, []);

  useEffect(() => {
    updateRightContainer(rightContainer);
  }, [rightContainer]);

  useEffect(() => {
    Analytics.sendEvent(VIEW_EVENTS.shop);

    const settings = queryClient.getQueryData('settings');
    if (!settings) {
      queryClient.setQueryData('settings', { shop, meta: { common: {}, clubs: [], safeLevels: [] } });
    }
  }, []);

  const onBuyCoins = () => {
    if (coinsPack) {
      buyCoins({
        id: coinsPack.id,
        name: coinsPack.name,
        price: convertToPlainNumbers(coinsPack.price),
      })
        .then((player) => {
          if (!player) {
            return;
          }

          pushNotification({
            type: 'success',
            title: t('shop.successful-payment'),
          });

          animateCoins({
            elementId: BUY_COINS_BUTTON_ID,
            increment: true,
            onAnimationEnd: () => {
              setCoinsPack(null);
              updatePlayer(player);
            },
            number: coinsPack?.coins,
          });
        })
        .catch((error) => {
          pushNotification({
            type: 'danger',
            title: error?.message || t('shop.error-purchase'),
          });
        });
    }
  };

  return (
    <>
      <Head>
        <title>{t('browser-title.shop')}</title>
      </Head>
      {isDesktop ? <PageLayoutHeader title={t('shop.page-title')} name="shop" withPlayerCoins /> : <InfoHeader />}
      <Shop coins={coins} topContent={!hasVIP(playerData?.player) ? <VIPBanner /> : null} onBuyCoins={setCoinsPack} />
      <WeeklyRewards />
      {coinsPack && (
        <BuyCoinsModal
          isOpen={!!coinsPack}
          product={coinsPack}
          onRequestClose={() => setCoinsPack(null)}
          onBuy={onBuyCoins}
          loading={loading}
        />
      )}
    </>
  );
};

const ShopPage: FC<AppTypes & ShopPageProps> & AppComponentType = (props) =>
  props.skeleton ? <ShopSkeletonPage {...props.shop} /> : <ShopRealPage {...props} />;

ShopPage.render = (props) => <CommonCustomApp {...props} />;

export async function getStaticProps(): Promise<{ props: ShopPageProps }> {
  try {
    const { shop } = await settingsRequest({
      pick: ['shop.purchaseCoins', 'shop.avatars', 'shop.boards', 'shop.emoticons', 'shop.phrases'],
    });

    return {
      props: { shop },
    };
  } catch (error) {
    return {
      props: {
        shop: {
          purchaseCoins: [],
          avatars: [],
          boards: [],
          emoticons: [],
          phrases: [],
        },
      },
    };
  }
}

export default ShopPage;
